import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Check, Gauge, Loader2, Zap } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Modal } from "@/components/editor/Dialogs";

export const scoreColor = (s) => (s >= 90 ? "border-emerald-200 bg-emerald-50 text-emerald-700" : s >= 75 ? "border-lime-200 bg-lime-50 text-lime-700" : s >= 60 ? "border-amber-200 bg-amber-50 text-amber-700" : "border-red-200 bg-red-50 text-red-600");
const ring = (s) => (s >= 90 ? "#10b981" : s >= 75 ? "#65a30d" : s >= 60 ? "#f59e0b" : "#ef4444");

export const SpeedDialog = ({ project, onClose }) => {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/projects/${project.id}/speed`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [project.id]);

  return (
    <Modal title="Nota de velocidade" onClose={onClose} testid="speed-dialog">
      {err && <p className="text-sm text-red-600">{err}</p>}
      {!d && !err && <div className="h-28 grid place-items-center"><Loader2 size={18} className="animate-spin text-slate-300" /></div>}
      {d && (
        <>
          <div className="flex items-center gap-4">
            <div className="relative h-24 w-24 shrink-0 grid place-items-center rounded-full" style={{ background: `conic-gradient(${ring(d.score)} ${d.score * 3.6}deg, #e9eef5 0)` }}>
              <div className="h-[76px] w-[76px] rounded-full bg-white grid place-items-center">
                <span data-testid="speed-score" className="font-display font-bold text-2xl leading-none">{d.score}</span>
                <span className="font-mono text-[10px] text-slate-400">nota {d.grade}</span>
              </div>
            </div>
            <div className="min-w-0">
              <p className="font-display font-semibold truncate">{d.name}</p>
              <p className="text-xs text-slate-500 mt-0.5">Peso {d.weight >= 1048576 ? `${(d.weight / 1048576).toFixed(2)} MB` : `${Math.round(d.weight / 1024)} KB`} · {d.metrics.images} imagem(ns) · {d.metrics.scripts} script(s) · {d.metrics.iframes} iframe(s)</p>
              <p className="text-[11px] text-slate-400 mt-1">A nota cai conforme o que atrasa o carregamento. Cada dica mostra quantos pontos você recupera.</p>
            </div>
          </div>

          {d.tips.length > 0 && (
            <div className="mt-5 space-y-2" data-testid="speed-tips">
              {d.tips.map((t) => (
                <div key={t.id} data-testid={`speed-tip-${t.id}`} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={14} className="text-[var(--orange)] mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-[var(--ink)] flex items-center justify-between gap-2">{t.title}<span className="font-mono text-[10px] text-slate-400 shrink-0">+{t.impact} pts</span></div>
                      <p className="text-xs text-slate-500 mt-0.5">{t.detail}</p>
                      {t.action === "optimize" && <button data-testid={`speed-fix-${t.id}`} onClick={() => nav(`/editor/${project.id}?optimize=1`)} className="mt-2 inline-flex items-center gap-1 rounded-lg bg-[var(--blue)] text-white px-2.5 py-1 text-[11px] font-medium hover:bg-[var(--ink)]"><Zap size={11} /> Otimizar imagens</button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {d.good.length > 0 && (
            <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-100 p-3" data-testid="speed-good">
              {d.good.map((g) => <div key={g} className="flex items-center gap-2 text-xs text-emerald-800"><Check size={12} /> {g}</div>)}
            </div>
          )}

          {d.tips.length === 0 && <p className="mt-4 text-sm text-emerald-700 flex items-center gap-2"><Gauge size={15} /> Nada a melhorar por aqui — essa página está voando.</p>}
        </>
      )}
    </Modal>
  );
};
